import React, { useState } from 'react';
import { object, string } from 'yup';
import { useFormik } from 'formik';

// Types
import { ElementSize } from '../../types/element-size';

// Constants
import { EMAIL_LENGTH_LIMITS, LENGTH_MFA_CODE } from '../../constants/common';

// Components
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import InputControl from '../InputControl/InputControl';
import IconEnvelope from '../Icons/IconEnvelope/IconEnvelope';

type ProfileEmailFormProps = {
  email: string;
  changeEmail: (email: string) => Promise<boolean>;
  verifyEmail: (data: { code: string, email: string }) => Promise<boolean>;
};

const ProfileEmailForm: React.FC<ProfileEmailFormProps> = ({ email, changeEmail, verifyEmail }) => {
  const [isAvailableCode, setIsAvailableCode] = useState(false);

  const updateEmail = async (email: string) => {
    const isSuccess = await changeEmail(email);
    if (isSuccess) setIsAvailableCode(true);
  };

  const checkCode = async (formData: { email: string, code: string }) => {
    const isSuccess = await verifyEmail(formData);
    if (isSuccess) setIsAvailableCode(false);
  };

  const formik = useFormik({
    initialValues: {
      email: email,
      code: '',
    },
    validationSchema: object({
      email: string()
        .email('Invalid email format')
        .min(EMAIL_LENGTH_LIMITS.MIN, `Minimum ${EMAIL_LENGTH_LIMITS.MIN} characters required`)
        .max(EMAIL_LENGTH_LIMITS.MAX, `Maximum ${EMAIL_LENGTH_LIMITS.MAX} characters required`)
        .required('Required'),
      ...(isAvailableCode
        ? {
          code: string()
            .required('Required')
            .length(LENGTH_MFA_CODE, `The MFA code must be ${LENGTH_MFA_CODE} digits long`),
        }
        : {}),
    }),
    onSubmit: async (values) => {
      const { email, code } = values;

      if (isAvailableCode) {
        await checkCode({ email, code })
      } else {
        await updateEmail(email)
      }
    },
  });

  const isDisabledBtn = isAvailableCode ? Boolean(formik.touched.code && formik.errors.code) : Boolean(formik.touched.email && formik.errors.email)

  const resetNewData = () => {
    setIsAvailableCode(false);
    formik.resetForm()
  }

  return (
    <form className='c-profile-email-form' onSubmit={formik.handleSubmit}>
      <div className="flex flex-col gap-5.5 sm:flex-row">
        <div className="flex-auto">
          <InputControl
            label='Email'
            type="email"
            name='email'
            placeholder="Enter your email"
            iconRight={<IconEnvelope size={ElementSize.MD} />}
            disabled={isAvailableCode}
            value={formik.values.email}
            customOnChange={formik.handleChange}
            onBlur={formik.handleBlur}
            showError={formik.touched.email && formik.errors.email}
            errorText={formik.errors.email}
          />
        </div>
        {isAvailableCode && (<div className="flex-auto">
          <InputControl
            label='Code'
            type="code"
            name='code'
            placeholder="Enter your code"
            value={formik.values.code}
            customOnChange={formik.handleChange}
            onBlur={formik.handleBlur}
            showError={formik.touched.code && formik.errors.code}
            errorText={formik.errors.code}
          />
        </div>)}
      </div>
      <div className="flex justify-end gap-4.5">
        <InteractiveButton
          text='Cancel'
          customType='bg-zinc-100 text-black border border-stroke border-zinc-300 hover:bg-zinc-200 hover:border-zinc-300 disabled:bg-zinc-400 disabled:border-zinc-600'
          className="text-black"
          onClick={resetNewData}
          disabled={formik.isSubmitting}
        />
        <InteractiveButton
          text={(isAvailableCode) ? 'Verify' : 'Save'}
          disabled={isDisabledBtn}
          submit
          isLoading={formik.isSubmitting}
        />
      </div>
    </form>
  )
}

export default ProfileEmailForm;
